import {
  Component,
  inject,
  model,
  OnDestroy,
  OnInit,
  output,
  QueryList,
  signal,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { L10N_LOCALE } from 'angular-l10n';
import { defer, forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';
import { MapSelection } from 'src/app/alert/models';
import { AlertGeofencesHelper } from 'src/app/alert/shared/alert-model/helpers/alert-geofences-helper';
import { AppConfig } from 'src/app/app.config';
import { AccessRecipe, Facility, LatLng, MapApi, ShapeImageResponse, FeatureCollection } from 'src/app/hickory-api';
import { LogHelper } from 'src/app/shared/logging/log-helper';
import { FeatureCollection as GeoJsonFeatureCollection } from 'geojson';
import { FacilitiesService, Order } from 'src/app/handweave-api';
import { ApiConfig } from '../../api-config/api-config';
import { calculateFeatureCollectionCenter, fetchAllItems } from '../../model/utility';
import { AccessRecipeHelper } from '../../model/accessrecipe-helper';
import { Filter, FilterDelimiter, FilterField } from '../../filtered-search/filtered-search.component';
import { LazyCache } from '../../cache/cache';
import { TimeSpan } from '../../model/date.helper';
import { PageEvent } from '@angular/material/paginator';
import {
  MapsApiLoader,
  POLYGON_COLORS,
  addFeatureCollectionToMap,
  MapMarker,
  centerAndFitBounds,
} from 'src/app/shared/google-maps';
import { GoogleMap, MapAdvancedMarker, MapInfoWindow } from '@angular/google-maps';
import { FacilityHelper } from 'src/app/contacts/facility-details/facility-helper';
import { BaseHref } from 'src/app/shared/base-href.helper';

export interface SelectedArea {
  id?: string;
  name?: string;
  geofences?: FeatureCollection;
  imageUrl?: string;
  context?: any;
}
type PreviewState = { show: boolean; page: number; pageSize: number; data: Facility[]; totalCount: number };

const DEFAULT_PAGE_SIZE = 10;
const FILTER_DELIMITER: FilterDelimiter = '||';

@Component({
  selector: 'app-facilities-map',
  templateUrl: './facilities-map.component.html',
  styleUrl: './facilities-map.component.scss',
  standalone: false,
})
export class FacilitiesMapComponent implements OnInit, OnDestroy {
  @ViewChild(GoogleMap) map: GoogleMap;
  @ViewChild(MapInfoWindow, { static: false }) infoWindow: MapInfoWindow;
  @ViewChildren(MapAdvancedMarker) mapAdvancedMarkers: QueryList<MapAdvancedMarker>;

  // Inputs & Outputs
  area = model<SelectedArea>();
  areaFacilities = model<Facility[]>([]);
  facilityFilters = model<AccessRecipe[]>([]);
  loading = output<boolean>();

  

  // Signals
  mapError = signal<{ message: string; area: SelectedArea } | null>(null);
  mapLoading = signal<boolean>(false);
  mapFacilitiesLoading = signal<boolean>(false);
  previewPage = signal<PreviewState>({ show: false, page: 0, pageSize: DEFAULT_PAGE_SIZE, data: [], totalCount: 0 });
  facilities = signal<Facility[]>([]);
  filters = signal<AccessRecipe[]>([]);
  selectedArea = signal<SelectedArea | undefined>(undefined);

  // Services
  readonly locale = inject(L10N_LOCALE);
  readonly mapApi = inject(MapApi);
  readonly facilitiesApi = inject(FacilitiesService);
  readonly mapsLoader = inject(MapsApiLoader);

  // State
  private readonly destroy$ = new Subject<void>();
  private waitingForMap = false;
  urls: Map<string, string> = new Map();
  defaultFilters: Map<string, Filter[]> = new Map();
  filterFields: FilterField[] = [
    {
      field: 'locationType',
      localizedLabel: 'Facility Type',
      type: 'select',
      valuesFunc: (field) => this.fieldSearch(field),
    },
  ];
  mapOptions: google.maps.MapOptions = {
    zoom: 10,
    streetViewControl: false,
    mapTypeControl: false,
    zoomControl: false,
    disableDoubleClickZoom: true,
    scrollwheel: true,
    clickableIcons: false,
  };
  markers: MapMarker[] = [];
  selectedMarker: MapMarker = null;
  private dataLayers: google.maps.Data[] = [];
  center = signal<LatLng | null>(null);

  childMapWindow: Window;
  selectedMap: MapSelection;
  selectedMapContext: string;

  ngOnInit(): void {
    if (this.area()) {
      this.selectedMap = this.area();
      this.onMapSelection(this.area());
    }

    window.addEventListener('message', this.receiveMessage);
    this.mapsLoader.apiLoaded$.subscribe((loaded) => {
      if (!loaded) return;

      this.setMapCenter();

      // Don't load markers here since facilities are not yet loaded
      // Markers will be loaded when facilities are available in onMapSelection
    });
    // Initialize previewPage after component is ready
    this.previewPage.set(this.defaultPreview());

    // Emit initial values
    this.emitCurrentValues();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    window.removeEventListener('message', this.receiveMessage);

    for (const dataLayer of this.dataLayers) {
      dataLayer?.setMap(null);
    }

    this.dataLayers = [];
  }

  setMapCenter() {
    if (this.selectedArea()?.geofences) {
      const center = calculateFeatureCollectionCenter(this.selectedArea().geofences);

      if (
        center &&
        typeof center.lat === 'number' &&
        typeof center.lng === 'number' &&
        !Number.isNaN(center.lat) &&
        !Number.isNaN(center.lng) &&
        center.lat >= -90 &&
        center.lat <= 90 &&
        center.lng >= -180 &&
        center.lng <= 180
      ) {
        this.center.set(center);
      } else {
        // Set a default center if the calculated center is invalid
        this.center.set({ lat: 39.8283, lng: -98.5795 }); // Center of USA
      }
    } else {
      // Set a default center if no area is selected
      this.center.set({ lat: 39.8283, lng: -98.5795 }); // Center of USA
    }
  }

  onLaunchMap(map?: any): void {
    this.waitingForMap = true;
    const url = AppConfig.hosts.hickoryApp + '/map?auth=SSO';
    this.selectedMapContext = map ? JSON.stringify(map) : undefined;
    this.childMapWindow = window.open(url, 'AlertSense.Balsa.Esri');
  }

  onMapInitialized(map: google.maps.Map) {
    const bounds = new google.maps.LatLngBounds();

    if (this.selectedArea()?.geofences) {
      try {
        const data = addFeatureCollectionToMap(this.toGeoJsonFeatureCollection(this.selectedArea().geofences), map, {
          strokeColor: POLYGON_COLORS[0],
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: POLYGON_COLORS[0],
          fillOpacity: 0.15,
        });

        data.forEach((feature) => {
          const geometry = feature.getGeometry();
          if (geometry) {
            geometry.forEachLatLng((point) => bounds.extend(point));
          }
        });

        this.dataLayers.push(data);
      } catch (error) {
        console.error('Error adding geofence data:', error);
      }
    }

    for (const marker of this.markers) {
      this.extendMapBounds(bounds, marker.lat, marker.lng);
    }

    // If bounds are empty (no valid coordinates), set a default center
    if (bounds.isEmpty()) {
      // Set default center (you can adjust these coordinates as needed)
      map.setCenter(new google.maps.LatLng(39.8283, -98.5795)); // Center of USA
      map.setZoom(4);

      // If we have markers but no bounds (e.g., markers loaded after map initialization),
      // try to center on the first valid marker
      if (this.markers.length > 0) {
        const firstMarker = this.markers[0];
        if (
          typeof firstMarker.lat === 'number' &&
          typeof firstMarker.lng === 'number' &&
          !Number.isNaN(firstMarker.lat) &&
          !Number.isNaN(firstMarker.lng)
        ) {
          map.setCenter(new google.maps.LatLng(firstMarker.lat, firstMarker.lng));
          map.setZoom(10);
        }
      }
    } else {
      centerAndFitBounds(map, bounds, this.markers, this.dataLayers);
    }
  }

  toGeoJsonFeatureCollection(featureCollection: FeatureCollection): GeoJsonFeatureCollection {
    // Convert FeatureCollection to GeoJsonFeatureCollection using JSON serialization
    const jsonString = JSON.stringify(featureCollection);
    return JSON.parse(jsonString) as GeoJsonFeatureCollection;
  }

  toAddress(facility: Facility): string {
    return FacilityHelper.getAddressParts(facility).join(', ');
  }

  loadMapMarkers() {
    const markers: MapMarker[] = [];
    for (const facility of this.facilities() || []) {
      if (
        facility.location &&
        typeof facility.location.latitude === 'number' &&
        typeof facility.location.longitude === 'number' &&
        !Number.isNaN(facility.location.latitude) &&
        !Number.isNaN(facility.location.longitude) &&
        facility.location.latitude >= -90 &&
        facility.location.latitude <= 90 &&
        facility.location.longitude >= -180 &&
        facility.location.longitude <= 180
      ) {
        markers.push({
          id: facility.id,
          lat: facility.location.latitude,
          lng: facility.location.longitude,
          icon: this.createMarkerIcon(`${BaseHref.get()}resources/images/facility_marker.svg`, 40, 40, 'Facility'),
          title: facility.name,
          subtitle: FacilityHelper.getAddressParts(facility).join(', '),
        });
      }
    }

    this.markers = markers;

    // If the map is already initialized, refresh it with the new markers
    if (this.map?.googleMap) {
      this.refreshMapWithMarkers();
    }
  }

  extendMapBounds(bounds: google.maps.LatLngBounds, lat: number, lng: number) {
    // Check for valid numeric coordinates (including 0)
    if (
      typeof lat !== 'number' ||
      typeof lng !== 'number' ||
      Number.isNaN(lat) ||
      Number.isNaN(lng) ||
      lat < -90 ||
      lat > 90 ||
      lng < -180 ||
      lng > 180
    ) {
      return;
    }
    bounds.extend(new google.maps.LatLng(lat, lng));
  }

  onMarkerClick(marker: MapMarker) {
    this.selectedMarker = marker;
    const mapAdvancedMarker = this.mapAdvancedMarkers.find((m) => {
      const markerLat = m.getAnchor().position?.lat;
      const markerLng = m.getAnchor().position?.lng;
      return markerLat === marker.lat && markerLng === marker.lng;
    });
    if (!mapAdvancedMarker) return;
    this.infoWindow.open(mapAdvancedMarker);
  }

  onMapClick(_event: google.maps.MapMouseEvent): void {
    // Map click handler - can be extended for future functionality
  }

  onMapError(error: any): void {
    console.error('Google Maps error:', error);
    this.mapError.set({
      message: 'Map failed to load: ' + (error?.message || 'Unknown error'),
      area: this.selectedArea(),
    });
  }

  createMarkerIcon(src: string, height: number, width: number, alt: string) {
    return Object.assign(document.createElement('img'), { src, height, width, alt });
  }

  private refreshMapWithMarkers(): void {
    if (!this.map?.googleMap) return;

    // Clear existing data layers
    for (const dataLayer of this.dataLayers) {
      dataLayer?.setMap(null);
    }
    this.dataLayers = [];

    // Re-initialize the map with current data
    this.onMapInitialized(this.map.googleMap);
  }

  private readonly receiveMessage = (message: MessageEvent): void => {
    if (message.data.response === 'ready' && this.selectedMapContext) {
      this.childMapWindow.postMessage(this.selectedMapContext, AppConfig.hosts.hickoryApp);
    }

    if (!this.waitingForMap) return;

    this.handleMapSelectionMessage(message);
  };

  private handleMapSelectionMessage(message?: MessageEvent): void {
    if (!this.isValidMapMessage(message)) return;

    try {
      const mapSelectionData: MapSelection = JSON.parse(message.data);
      LogHelper.debug('AlertAdvancedFacilities', 'Map selection data', mapSelectionData);
      // We'll try to fix the GeoJSON data, but if it fails for whatever reason we'll use the original data.
      try {
        AlertGeofencesHelper.fixFeatureCollection(mapSelectionData.geoJSON);
        mapSelectionData.geoJSON.features = AlertGeofencesHelper.fixInvalidElmFeatures(
          mapSelectionData.geoJSON.features,
        );
      } catch (error) {
        LogHelper.debug('AlertAdvancedFacilities', 'Error fixing map selection. Using original data.', error);
      }

      const newMapArea = this.createNewArea(mapSelectionData);
      this.selectedMap = newMapArea;

      this.onMapSelection(newMapArea);
    } catch (error) {
      LogHelper.debug('AlertAdvancedFacilities', 'Error parsing map selection', error);
    }

    this.waitingForMap = false;
  }

  onMapSelection(area?: SelectedArea): void {
    if (!area) {
      this.mapError.set(null);
      // Emit undefined values when clearing
      this.area.set(undefined);
      this.areaFacilities.set([]);
      this.facilityFilters.set([]);
      return;
    }
    this.selectedArea.set(area);
    this.mapError.set(null);
    this.loading.emit(true);

    // Emit the selected area immediately
    this.area.set(area);

    forkJoin([this.getMapFacilities(area, this.filters()), this.getMapPreview(area.geofences)])
      .pipe(
        tap(([facilities, preview]) => {
          this.facilities.set(facilities);
          this.urls.set(area.id, preview.ImageUrl);

          // Load map markers after facilities are loaded
          this.loadMapMarkers();

          // Emit facilities and filters after loading
          this.areaFacilities.set(facilities);
          this.facilityFilters.set(this.filters());
          this.previewPage.set(this.defaultPreview());
        }),
        catchError((_) => {
          this.facilities.set([]);
          this.urls.delete(area.id);
          this.mapError.set({ message: 'Failed to load facilities. Please try again.', area });

          // Emit empty arrays on error
          this.areaFacilities.set([]);
          this.facilityFilters.set([]);
          return of(null);
        }),
        finalize(() => {
          this.loading.emit(false);
        }),
      )
      .subscribe();
  }

  onPreviewMapFacilities(): void {
    const area = this.selectedArea();
    if (!area) {
      return;
    }

    this.previewPage.set({
      show: !this.previewPage().show,
      page: 0,
      pageSize: DEFAULT_PAGE_SIZE,
      data: this.facilities().slice(0, DEFAULT_PAGE_SIZE),
      totalCount: this.facilities().length,
    });
  }

  onRemoveMap(): void {
    this.urls.delete(this.selectedArea().id);
    this.previewPage.set(this.defaultPreview());
    this.selectedArea.set(undefined);

    // Emit cleared values
    this.emitCurrentValues();
  }

  private isValidMapMessage(message?: MessageEvent): boolean {
    return !!(message?.data && typeof message.data === 'string' && message.data !== 'unchanged');
  }

  private createNewArea(selection: MapSelection): SelectedArea {
    return {
      id: crypto.randomUUID(),
      name: 'Facilities',
      geofences: selection.geoJSON,
      imageUrl: null,
      context: selection.context,
    };
  }

  private getMapFacilities(area: SelectedArea, filters: AccessRecipe[]): Observable<Facility[]> {
    if (!area?.geofences) {
      return of([]);
    }

    return defer(() => {
      this.mapFacilitiesLoading.set(true);
      return fetchAllItems<Facility>(
        (page, pageSize, items) =>
          this.facilitiesApi
            .facilitiesGetBySearch({
              tenantId: ApiConfig.tenantId,
              page,
              pageSize,
              search: {
                featureCollection: area.geofences as never,
                filters: [{ oneOf: AccessRecipeHelper.toSearchFilters(filters) }],
                paging: { page, pageSize, searchAfter: items?.length > 0 ? [items.at(-1).id] : null },
                sort: [{ field: 'id', sortOrder: Order.Asc }],
              },
            })
            .pipe(
              catchError((error) => {
                LogHelper.debug('AlertAdvancedFacilities', 'Error getting map facilities', error);
                return of(null);
              }),
            ),
        500,
        5000,
        10,
      );
    }).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.mapFacilitiesLoading.set(false)),
    );
  }

  private getMapPreview(area: FeatureCollection): Observable<ShapeImageResponse> {
    // Skip API call if area is empty or null
    if (!area) {
      return of({ ImageUrl: null } as ShapeImageResponse);
    }

    this.mapLoading.set(true);
    // Convert GeoJson.FeatureCollection to Tamarack.FeatureCollection
    return this.mapApi
      .getImagePreview({
        geoJson: area,
        height: 200,
        width: 300,
      })
      .pipe(finalize(() => this.mapLoading.set(false)));
  }
  private readonly searchCache = new LazyCache<string[]>();
  private fieldSearch(field: string): (input: string) => Observable<string[]> {
    return (input: string) => {
      const query = input.trim() + '*';
      const cacheKey = `${field}:${query}`;
      const cached = this.searchCache.get(cacheKey);

      if (cached) {
        return of(cached);
      }

      return this.searchCache.fetchWithCache(
        cacheKey,
        this.facilitiesApi
          .facilitiesGetPossibleValuesByPage({
            tenantId: ApiConfig.tenantId,
            page: 1,
            pageSize: 500,
            field,
            query,
          })
          .pipe(
            catchError((error) => {
              LogHelper.debug('AlertFacilitiesMap', 'Error fetching field values:', error);
              return of([]);
            }),
          ),
        { expirationMs: TimeSpan.fromMinutes(10) },
      );
    };
  }

  onFilterChange(filters: Filter[]): void {
    const area = this.selectedArea();
    if (!area) return;
    this.previewPage.set(this.defaultPreview());
    this.updateFiltersForArea(area, filters);

    // Emit updated filters immediately (convert to AccessRecipe)
    this.facilityFilters.set(this.filters());

    this.onMapSelection(area);
  }

  private defaultPreview(): PreviewState {
    return {
      show: false,
      page: 0,
      pageSize: DEFAULT_PAGE_SIZE,
      data: this.facilities().slice(0, DEFAULT_PAGE_SIZE),
      totalCount: 0,
    };
  }

  private updateFiltersForArea(area: SelectedArea, filters: Filter[]): void {
    this.filters.set(AccessRecipeHelper.fromFilters(filters, (value) => value.split(FILTER_DELIMITER)));
    this.defaultFilters.set(area.id, filters);
  }

  private emitCurrentValues(): void {
    // Sync internal signals with models
    this.area.set(this.selectedArea());
    this.areaFacilities.set(this.facilities());
    this.facilityFilters.set(this.filters());
  }

  pagePreview(event: PageEvent): void {
    const start = event.pageIndex * event.pageSize;
    const end = start + event.pageSize;
    this.previewPage.set({
      show: this.previewPage().show,
      page: event.pageIndex + 1,
      pageSize: event.pageSize,
      data: this.facilities().slice(start, end),
      totalCount: this.previewPage().totalCount,
    });
  }
}
